/**
 * Mapping ruoli CRA → privilegi portale TC (lato portale, solo UI gating).
 *
 * ATTENZIONE: questi permessi servono esclusivamente a mostrare/nascondere
 * elementi UI. L'autorizzazione effettiva è applicata server-side dai servizi
 * Node tramite @tenaciousc/auth (con verifica firma JWT). Non fare affidamento
 * su questo mapping per decisioni di sicurezza.
 *
 * LIMITAZIONE DOCUMENTATA: i ruoli custom (non in ROLE_PERMISSIONS) ottengono
 * zero permessi lato client — il gating UI mostra la shell base e le sezioni
 * compaiono per-pagina in base alla gestione dei 403 server-side, come oggi.
 * L'autorizzazione effettiva rimane sempre server-side.
 */

import type { CraRole, TcPermission } from './types.js';

/** Catalogo locale completo di tutti i privilegi TC (per admin bypass). */
export const ALL_TC_PERMISSIONS_UI: readonly TcPermission[] = [
  'VIEW_PORTFOLIO',
  'PORTFOLIO_MANAGEMENT',
  'VIEW_VULNERABILITY',
  'VULNERABILITY_ANALYSIS',
  'VULNERABILITY_MANAGEMENT',
  'VIEW_POLICY_VIOLATION',
  'POLICY_VIOLATION_ANALYSIS',
  'POLICY_MANAGEMENT',
  'BOM_UPLOAD',
  'ACCESS_MANAGEMENT',
  'TAG_MANAGEMENT',
  'SYSTEM_CONFIGURATION',
  'VIEW_AUDIT_LOG',
];

/**
 * Mapping ruolo CRA → privilegi portale TC (specchio di packages/auth/src/roles.ts).
 * Solo i ruoli built-in hanno un mapping locale; i ruoli custom usano zero permessi
 * (vedi limitazione documentata sopra).
 */
export const ROLE_PERMISSIONS: Record<string, readonly TcPermission[]> = {
  security_analyst: [
    'VIEW_PORTFOLIO',
    'VIEW_VULNERABILITY',
    'VULNERABILITY_ANALYSIS',
    'VIEW_POLICY_VIOLATION',
    'POLICY_VIOLATION_ANALYSIS',
    'BOM_UPLOAD',
  ],
  developer: ['VIEW_PORTFOLIO', 'PORTFOLIO_MANAGEMENT', 'BOM_UPLOAD', 'VIEW_VULNERABILITY'],
  analyst_supervisor: [
    'VIEW_PORTFOLIO',
    'VIEW_VULNERABILITY',
    'VULNERABILITY_ANALYSIS',
    'VIEW_POLICY_VIOLATION',
    'POLICY_VIOLATION_ANALYSIS',
    'VULNERABILITY_MANAGEMENT',
    'BOM_UPLOAD',
  ],
  // NB: nessuna chiave 'admin' — il bypass è code-level in rolesToPermissions.
};

/**
 * Espande i ruoli nell'insieme dei privilegi portale TC effettivi (unione).
 * Bypass hardwired: il ruolo 'admin' restituisce SEMPRE tutti i privilegi,
 * indipendentemente dalla mappa — specchio di permissionsFromMap in packages/auth.
 * Usato internamente da hasPermission per derivare i permessi dai claim JWT.
 */
export function rolesToPermissions(roles: readonly CraRole[]): Set<TcPermission> {
  // Bypass hardwired: admin ha SEMPRE tutti i privilegi (specchio di packages/auth).
  if (roles.includes('admin')) return new Set(ALL_TC_PERMISSIONS_UI);
  const perms = new Set<TcPermission>();
  for (const role of roles) {
    for (const p of ROLE_PERMISSIONS[role] ?? []) {
      perms.add(p);
    }
  }
  return perms;
}

/**
 * Verifica se i claim dell'utente includono il permesso richiesto.
 * Derive il set di permessi dai ruoli CRA presenti nei claim.
 *
 * @param roles - Ruoli CRA estratti dal JWT (campo `roles`)
 * @param required - Permesso TC da verificare
 * @returns true se almeno uno dei ruoli include il permesso
 */
export function hasPermission(roles: readonly CraRole[], required: TcPermission): boolean {
  return rolesToPermissions(roles).has(required);
}
